<template>
  <div 
    class="table-card" 
    @click="$emit('click')" 
    :class="{ 
      'table-locked': tab.inUse,
      'table-high-value': isHighValue 
    }"
  >
    <!-- Status Badge -->
    <div class="status-badge" v-if="tab.inUse">
      <font-awesome-icon icon="lock" />
      <span>In Use</span>
    </div>
    
    <!-- Table Name -->
    <div class="table-name">
      <h3>{{ tab.Name }}</h3>
    </div>
    
    <!-- Table Details -->
    <div class="table-details">
      <div class="detail-row">
        <div class="detail-item">
          <font-awesome-icon icon="user" class="detail-icon" />
          <span class="detail-text">{{ assignedUser }}</span>
        </div>
        <span class="status-pill" :class="isOffline ? 'status-offline' : 'status-online'">
          {{ isOffline ? 'Offline' : 'Online' }}
        </span>
      </div>
      
<!--      <div class="detail-row">-->
<!--        <div class="detail-item">-->
<!--          <font-awesome-icon icon="receipt" class="detail-icon" />-->
<!--          <span class="detail-text">Sale #{{ saleNumber }}</span>-->
<!--        </div>-->
<!--        <div class="detail-item meta-text">-->
<!--          <font-awesome-icon icon="calendar" class="detail-icon" />-->
<!--          <span class="detail-text">{{ createdDate }}</span>-->
<!--        </div>-->
<!--      </div>-->

      <div class="detail-row">
        <div class="detail-item">
          <font-awesome-icon icon="shopping-bag" class="detail-icon" />
          <span class="detail-text">{{ tab.items.length }} item{{ tab.items.length !== 1 ? 's' : '' }}</span>
        </div>
        <div v-if="isShopOrder" class="shop-badge">shop.co.zm</div>
        <div class="tip-badge" v-if="tip > 0">
          <font-awesome-icon icon="coins" />
          {{ $utils.formatMoney(tip) }}
        </div>
      </div>
    </div>
    
    <!-- Total Amount -->
    <div class="table-total">
      <span class="total-label">Total</span>
      <h2 class="total-amount">{{ $utils.formatMoney(getSaleTotal(tab.items) + tip) }}</h2>
    </div>

    <!-- High Value Indicator -->
<!--    <div class="high-value-badge" v-if="isHighValue">-->
<!--      <font-awesome-icon icon="star" />-->
<!--    </div>-->
  </div>
</template>

<script>
import {Storage} from "@/services/Storage";

export default {
  name: "SingleTable",
  beforeMount() {
    this.getTipTotal();
  },
  props: {
    tab: {type: Object, required: true}
  },
  data() {
    return {
      tip: 0
    }
  },
  computed: {
    isHighValue() {
      // Consider high value if total > 1000 (adjust threshold as needed)
      const total = this.getSaleTotal(this.tab.items) + this.tip;
      return total > 1000;
    },
    saleNumber() {
      return this.tab.saleNumber || this.tab.SaleNum || 'N/A';
    },
    assignedUser() {
      return this.tab.user || this.tab.User || 'Unassigned';
    },
    isOffline() {
      return this.tab.isOffline === true;
    },
    isShopOrder() {
      return (this.tab.Name || '').toUpperCase().startsWith('SHOP.CO.ZM ORDER');
    },
    isQuote() {
      return !!(this.tab.customer && this.tab.customer !== '');
    },
    createdDate() {
      const raw = this.tab.createdAt || this.tab.CreatedAt || this.tab.createdDate || this.tab.CreatedDate ||
        this.tab.created || this.tab.DateCreated || this.tab.date || this.tab.Date || this.tab.created_on || this.tab.createdOn;
      return this.formatDate(raw);
    },
  },
  methods: {
    formatDate(value) {
      if (!value) return 'N/A';
      const date = value instanceof Date ? value : new Date(value);
      if (Number.isNaN(date.getTime())) return 'N/A';
      return date.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    getSaleTotal(array) {
      let total = 0;
      for (let i = 0; i < array.length; i++) {
        total += array[i].Line;
        let instructions = array[i].instructions;
        if (instructions) {
          for (let k = 0; k < instructions.length; k++) {
            let price = instructions[k].Price;
            total += price;
          }
        }
      }
      return total;
    },
    async getTipTotal() {
      let saleTotal = this.getSaleTotal(this.tab.items);
      let settings = await Storage.getDeviceSettings();
      let tip = 0;
      if (settings && settings.AutoTip > 0) {
        tip = Number(settings.AutoTip);
        tip = (tip / 100) * saleTotal;
      }
      this.tip = tip;
    },
  },
}
</script>

<style lang="scss" scoped>
.table-card {
  background: var(--card-bg);
  border: var(--card-border);
  box-sizing: border-box;
  border-radius: 12px;
  padding: 1rem;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: var(--transition-default);
  box-shadow: var(--card-shadow);
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-height: 160px;

  // Gradient overlay for normal tables
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 6px;
    background: linear-gradient(135deg, var(--accent-primary) 0%, var(--accent-primary-dark) 100%);
    opacity: 0.8;
  }

  // Locked table styling
  &.table-locked {
    background: linear-gradient(135deg, rgba(220, 53, 69, 0.05) 0%, rgba(220, 53, 69, 0.1) 100%);
    border-color: var(--danger);
    border-width: 2px;

    &::before {
      background: linear-gradient(135deg, var(--danger) 0%, #c82333 100%);
      height: 8px;
    }

    .table-name h3 {
      color: var(--danger);
    }
  }

  // High value table styling
  &.table-high-value {
    border-color: #ffc107;
    
    &::before {
      background: linear-gradient(135deg, #ffc107 0%, #ff9800 100%);
    }
  }

  &:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 0 12px 24px rgba(var(--accent-electric-blue-rgb), 0.2);
    border-color: var(--accent-primary);
  }

  &:active {
    transform: translateY(-4px) scale(1.01);
  }
}

.status-badge {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: var(--danger);
  color: white;
  padding: 0.4rem 0.8rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(220, 53, 69, 0.4);
}

.table-name {
  margin-top: 0.5rem;
  
  h3 {
    font-size: 1.1rem;
    font-weight: 700;
    color: var(--text-primary);
    margin: 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.table-details {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  flex: 1;

  .detail-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
  }

  .detail-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: var(--text-secondary);
    font-size: 0.9rem;

    .detail-icon {
      color: var(--accent-primary);
      font-size: 1rem;
      width: 16px;
    }

    .detail-text {
      font-weight: 600;
    }
  }

  .meta-text {
    font-size: 0.8rem;
  }

  .tip-badge {
    background: rgba(var(--accent-electric-blue-rgb), 0.1);
    color: var(--accent-primary);
    padding: 0.3rem 0.6rem;
    border-radius: 12px;
    font-size: 0.8rem;
    font-weight: 700;
    display: flex;
    align-items: center;
    gap: 0.3rem;
  }
}

.status-pill {
  padding: 0.25rem 0.6rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  border: 1px solid transparent;
}

.status-online {
  background: rgba(50, 215, 75, 0.12);
  color: var(--success);
  border-color: rgba(50, 215, 75, 0.25);
}

.status-offline {
  background: rgba(255, 59, 48, 0.12);
  color: var(--danger);
  border-color: rgba(255, 59, 48, 0.25);
}

.shop-badge {
  background: rgba(165, 41, 255, 0.12);
  color: var(--accent-neon-purple);
  padding: 0.25rem 0.6rem;
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.quote-badge {
  background: rgba(255, 193, 7, 0.2);
  color: #b58100;
  padding: 0.25rem 0.6rem;
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.table-total {
  margin-top: auto;
  padding-top: 1rem;
  border-top: 2px solid rgba(var(--accent-electric-blue-rgb), 0.2);
  display: flex;
  justify-content: space-between;
  align-items: baseline;

  .total-label {
    font-size: 0.85rem;
    color: var(--text-secondary);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .total-amount {
    font-size: 1.25rem;
    font-weight: 700;
    color: var(--accent-primary);
    margin: 0;
    background: linear-gradient(135deg, var(--accent-primary) 0%, var(--accent-primary-dark) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
}




@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 2px 8px rgba(255, 193, 7, 0.4);
  }
  50% {
    transform: scale(1.1);
    box-shadow: 0 4px 16px rgba(255, 193, 7, 0.6);
  }
}

// Mobile optimizations
@media screen and (max-width: 768px) {
  .table-card {
    min-height: 140px;
    padding: 0.75rem;
  }

  .table-name h3 {
    font-size: 1rem;
  }

  .table-total .total-amount {
    font-size: 1.1rem;
  }

  .status-badge {
    font-size: 0.65rem;
    padding: 0.25rem 0.5rem;
  }
}
</style>
